import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";

import Badge from "../ui/badge/Badge";
import { useState, useEffect } from "react";
import Pagination from "../ui/pagination/Pagination";
import api from '../../services/api';

interface Withdrawal {
    id: string;
    withdrawalId: string;
    grossAmount: number;
    withdrawalFee: number;
    netAmount: number;
    status: string;
    createdAt: string;
    updatedAt: string;
    company: {
        companyId: string;
        fantasyName: string;
    };
}

interface WithdrawalResponse {
    withdrawals: Withdrawal[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    filters: {
        startDate?: string;
        endDate?: string;
        status?: string;
    };
}

interface FinanceTableProps {
    searchTerm?: string;
    statusFilter?: string;
    companyId: string;
}

export default function FinanceTable({ 
    searchTerm = "", 
    statusFilter = "", 
    companyId 
}: FinanceTableProps) {
    const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const itemsPerPage = 10;

    // Função para buscar saques
    const fetchWithdrawals = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: currentPage.toString(),
                limit: itemsPerPage.toString(),
            });

            if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);

            const url = `/api/withdrawals/company/${companyId}?${params}`;
            const response = await api.get(url);
            
            const data: { success: boolean; data: WithdrawalResponse } = response.data;
            
            if (data.success) {
                setWithdrawals(data.data.withdrawals);
                setTotalPages(data.data.totalPages);
                setTotal(data.data.total);
            }
        } catch (error) {
            console.error('Erro ao buscar saques:', error);
        } finally {
            setLoading(false);
        }
    };

    // Buscar saques quando componente monta ou filtros mudam
    useEffect(() => {
        if (companyId) {
            fetchWithdrawals();
        }
    }, [companyId, currentPage, statusFilter]);

    // Função para mudar de página
    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    // Função para formatar data
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Função para formatar valor
    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    // Função para obter cor do status
    const getStatusColor = (status: string): 'warning' | 'info' | 'success' | 'error' => {
        switch (status.toLowerCase()) {
            case 'requested':
                return 'warning';
            case 'processing':
                return 'info';
            case 'completed':
                return 'success';
            case 'failed':
            case 'cancelled':
                return 'error';
            default:
                return 'error';
        }
    };

    // Função para traduzir status
    const translateStatus = (status: string) => {
        switch (status.toLowerCase()) {
            case 'requested':
                return 'Solicitado';
            case 'processing':
                return 'Processando';
            case 'completed':
                return 'Concluído';
            case 'failed':
                return 'Falhou';
            case 'cancelled':
                return 'Cancelado';
            default:
                return status;
        }
    };

    // Filtrar por termo de busca (local)
    const filteredWithdrawals = withdrawals.filter(withdrawal => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            withdrawal.withdrawalId.toLowerCase().includes(searchLower) ||
            withdrawal.company.fantasyName.toLowerCase().includes(searchLower) ||
            formatCurrency(withdrawal.grossAmount).toLowerCase().includes(searchLower)
        );
    });

    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03]">
            <div className="max-w-full overflow-x-auto">
                <Table>
                    {/* Table Header */}
                    <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                        <TableRow>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                ID do Saque
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Data
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Valor
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Taxa
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Valor Líquido
                            </TableCell>
                            <TableCell
                                isHeader
                                className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                            >
                                Status
                            </TableCell>
                        </TableRow>
                    </TableHeader>

                    {/* Table Body */}
                    <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} className="px-5 py-8 text-center">
                                    <p className="text-gray-500 dark:text-gray-400">Carregando saques...</p>
                                </TableCell>
                            </TableRow>
                        ) : filteredWithdrawals.length > 0 ? (
                            filteredWithdrawals.map((withdrawal) => (
                                <TableRow key={withdrawal.id}>
                                    <TableCell className="px-5 py-3 text-start">
                                        <span className="block text-gray-500 text-theme-sm dark:text-gray-400">
                                            {withdrawal.withdrawalId}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-5 py-3 text-start">
                                        <div>
                                            <span className="block text-gray-500 text-theme-sm dark:text-gray-400">
                                                {formatDate(withdrawal.createdAt)}
                                            </span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="px-5 py-3 text-start">
                                        <span className="block font-medium text-gray-800 text-theme-sm dark:text-white/90">
                                            {formatCurrency(withdrawal.grossAmount)}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-5 py-3 text-start">
                                        <span className="block text-gray-500 text-theme-sm dark:text-gray-400">
                                            {formatCurrency(withdrawal.withdrawalFee)}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-5 py-3 text-start">
                                        <span className="block font-medium text-green-600 text-theme-sm dark:text-green-400">
                                            {formatCurrency(withdrawal.netAmount)}
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-5 py-3 text-start">
                                        <Badge
                                            size="sm"
                                            color={getStatusColor(withdrawal.status)}
                                        >
                                            {translateStatus(withdrawal.status)}
                                        </Badge>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} className="px-5 py-8 text-center">
                                    <div className="text-center">
                                        <p className="text-gray-500 dark:text-gray-400 mb-2">
                                            {searchTerm ? 'Nenhum saque encontrado para sua busca' : 'Nenhum saque encontrado'}
                                        </p>
                                        {total > 0 && (
                                            <p className="text-gray-400 text-sm dark:text-gray-500">
                                                Total de {total} saques na base de dados
                                            </p>
                                        )}
                                    </div>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                {totalPages > 1 && (
                    <div className="p-4 border-t border-gray-100 dark:border-white/[0.05]">
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}